/**
 * Synthèses « qualité vendeur » et « qualité acheteur » pour le détail admin utilisateur.
 * S’appuie sur les transactions / litiges / notations déjà renvoyés par `/admin/user-detail`
 * (fenêtre limitée côté API, typ. 200 dernières transactions).
 */

const MS_90D = 90 * 24 * 60 * 60 * 1000;

function parseMs(at) {
  const t = Date.parse(String(at || ""));
  return Number.isFinite(t) ? t : null;
}

function isWithinLast90Days(at, nowMs) {
  const t = parseMs(at);
  if (t == null) return false;
  return nowMs - t <= MS_90D;
}

const TERMINAL_SUCCESS = new Set(["fonds_debloques"]);
const TERMINAL_BAD = new Set(["rembourse", "annule"]);

function isTerminalSuccess(st) {
  return TERMINAL_SUCCESS.has(String(st || ""));
}
function isTerminalBad(st) {
  return TERMINAL_BAD.has(String(st || ""));
}
function isInProgress(st) {
  const s = String(st || "");
  return s && !isTerminalSuccess(s) && !isTerminalBad(s);
}

/**
 * @param {object} detail — payload `adminUserDetail` (summary, transactions, litiges, notations)
 * @returns {null | object}
 */
export function computeAdminSellerQuality(detail) {
  if (!detail || typeof detail !== "object") return null;
  const uid = String(detail.summary?.user_id || "").trim();
  if (!uid) return null;

  const transactions = Array.isArray(detail.transactions) ? detail.transactions : [];
  const litiges = Array.isArray(detail.litiges) ? detail.litiges : [];
  const notations = Array.isArray(detail.notations) ? detail.notations : [];

  const sellerTx = transactions.filter((t) => String(t.seller_id || "") === uid);
  if (sellerTx.length === 0) {
    return {
      has_seller_activity: false,
      seller_transaction_count: 0,
      alerts: [],
    };
  }

  const nowMs = Date.now();
  const txById = Object.fromEntries(sellerTx.map((t) => [t.id, t]).filter(([k]) => Boolean(k)));

  const sellerCompleted = sellerTx.filter((t) => isTerminalSuccess(t.statut));
  const sellerBadEnd = sellerTx.filter((t) => isTerminalBad(t.statut));
  const sellerOpen = sellerTx.filter((t) => isInProgress(t.statut));

  const litigesAsSeller = litiges.filter((l) => {
    const tid = l.transaction_id;
    if (!tid || !txById[tid]) return false;
    return String(txById[tid].seller_id || "") === uid;
  });

  let litigesOpenedByBuyer = 0;
  for (const l of litigesAsSeller) {
    const tx = txById[l.transaction_id];
    if (!tx?.buyer_id || !l.opened_by) continue;
    if (String(l.opened_by) === String(tx.buyer_id)) litigesOpenedByBuyer += 1;
  }

  const ratingsReceivedSeller = notations.filter((n) => {
    if (String(n.rated_user_id || "") !== uid || !n.transaction_id) return false;
    const tx = txById[n.transaction_id];
    return tx && String(tx.seller_id || "") === uid;
  });
  const stars = ratingsReceivedSeller
    .map((n) => Number(n.stars))
    .filter((x) => Number.isFinite(x) && x > 0);
  const avgStars = stars.length ? stars.reduce((a, b) => a + b, 0) / stars.length : null;

  const completedCount = sellerCompleted.length;
  const litigeCount = litigesAsSeller.length;
  const litigeRateVsCompleted = completedCount > 0 ? litigeCount / completedCount : null;

  const alerts = [];
  if (completedCount >= 3 && litigeRateVsCompleted != null && litigeRateVsCompleted >= 1 / 3) {
    alerts.push({
      level: "warn",
      text: "Taux de litiges élevé par rapport aux ventes finalisées (≥ 1 litige pour 3 ventes débloquées).",
    });
  }
  if (avgStars != null && stars.length >= 2 && avgStars < 2.5) {
    alerts.push({
      level: "warn",
      text: `Note moyenne faible en tant que vendeur (${avgStars.toFixed(1)}/5, ${stars.length} avis liés à des ventes).`,
    });
  }
  const denom = sellerTx.length;
  if (denom >= 5 && sellerBadEnd.length / denom >= 0.45) {
    alerts.push({
      level: "warn",
      text: "Part importante de ventes terminées en remboursement ou annulation.",
    });
  }
  if (litigesOpenedByBuyer >= 3 && completedCount >= 2 && litigesOpenedByBuyer / Math.max(completedCount, 1) >= 0.5) {
    alerts.push({
      level: "warn",
      text: "Plusieurs litiges ouverts par des acheteurs sur des ventes finalisées — à examiner.",
    });
  }

  return {
    has_seller_activity: true,
    seller_transaction_count: sellerTx.length,
    seller_completed_count: sellerCompleted.length,
    seller_refunded_or_cancelled_count: sellerBadEnd.length,
    seller_in_progress_count: sellerOpen.length,
    seller_litiges_count: litigeCount,
    seller_litiges_opened_by_buyer_count: litigesOpenedByBuyer,
    seller_litige_rate_vs_completed: litigeRateVsCompleted,
    seller_ratings_count: stars.length,
    seller_avg_rating: avgStars != null ? Math.round(avgStars * 10) / 10 : null,
    last_90d: {
      seller_transaction_count: sellerTx.filter((t) => isWithinLast90Days(t.created_at, nowMs)).length,
      seller_completed_count: sellerCompleted.filter((t) => isWithinLast90Days(t.created_at, nowMs)).length,
    },
    alerts,
  };
}

/**
 * @param {object} detail — payload `adminUserDetail`
 * @returns {null | object}
 */
export function computeAdminBuyerQuality(detail) {
  if (!detail || typeof detail !== "object") return null;
  const uid = String(detail.summary?.user_id || "").trim();
  if (!uid) return null;

  const transactions = Array.isArray(detail.transactions) ? detail.transactions : [];
  const litiges = Array.isArray(detail.litiges) ? detail.litiges : [];
  const notations = Array.isArray(detail.notations) ? detail.notations : [];

  const buyerTx = transactions.filter((t) => String(t.buyer_id || "") === uid);
  if (buyerTx.length === 0) {
    return {
      has_buyer_activity: false,
      buyer_transaction_count: 0,
      alerts: [],
    };
  }

  const nowMs = Date.now();
  const txById = Object.fromEntries(buyerTx.map((t) => [t.id, t]).filter(([k]) => Boolean(k)));

  const buyerCompleted = buyerTx.filter((t) => isTerminalSuccess(t.statut));
  const buyerBadEnd = buyerTx.filter((t) => isTerminalBad(t.statut));
  const buyerOpen = buyerTx.filter((t) => isInProgress(t.statut));

  const litigesAsBuyer = litiges.filter((l) => {
    const tid = l.transaction_id;
    if (!tid || !txById[tid]) return false;
    return String(txById[tid].buyer_id || "") === uid;
  });

  let litigesOpenedByBuyer = 0;
  let litigesOpenedBySeller = 0;
  for (const l of litigesAsBuyer) {
    const tx = txById[l.transaction_id];
    if (!l.opened_by || !tx) continue;
    if (String(l.opened_by) === String(tx.buyer_id)) litigesOpenedByBuyer += 1;
    else if (tx.seller_id && String(l.opened_by) === String(tx.seller_id)) litigesOpenedBySeller += 1;
  }

  const ratingsReceivedBuyer = notations.filter((n) => {
    if (String(n.rated_user_id || "") !== uid || !n.transaction_id) return false;
    const tx = txById[n.transaction_id];
    return tx && String(tx.buyer_id || "") === uid;
  });
  const stars = ratingsReceivedBuyer
    .map((n) => Number(n.stars))
    .filter((x) => Number.isFinite(x) && x > 0);
  const avgStars = stars.length ? stars.reduce((a, b) => a + b, 0) / stars.length : null;

  const completedCount = buyerCompleted.length;
  const litigeCount = litigesAsBuyer.length;
  const litigeRateVsCompleted = completedCount > 0 ? litigeCount / completedCount : null;

  const alerts = [];
  if (completedCount >= 3 && litigeRateVsCompleted != null && litigeRateVsCompleted >= 1 / 3) {
    alerts.push({
      level: "warn",
      text: "Taux de litiges élevé par rapport aux achats finalisés (débloqués) — à recouper avec le détail des litiges.",
    });
  }
  if (avgStars != null && stars.length >= 2 && avgStars < 2.5) {
    alerts.push({
      level: "warn",
      text: `Note moyenne faible en tant qu’acheteur (${avgStars.toFixed(1)}/5, ${stars.length} avis liés à des achats).`,
    });
  }
  const denom = buyerTx.length;
  if (denom >= 5 && buyerBadEnd.length / denom >= 0.45) {
    alerts.push({
      level: "warn",
      text: "Part importante d’achats terminés en remboursement ou annulation.",
    });
  }
  if (completedCount >= 3 && litigesOpenedByBuyer / Math.max(completedCount, 1) >= 0.34) {
    alerts.push({
      level: "warn",
      text: "Souvent en litige sur des achats finalisés, litiges ouverts par ce compte (acheteur) — comportement à examiner.",
    });
  }
  if (buyerTx.length >= 5 && litigesOpenedByBuyer >= 3 && litigesOpenedByBuyer / buyerTx.length >= 0.3) {
    alerts.push({
      level: "warn",
      text: "Nombre élevé de litiges ouverts par cet acheteur au regard du volume d’achats visibles.",
    });
  }

  return {
    has_buyer_activity: true,
    buyer_transaction_count: buyerTx.length,
    buyer_completed_count: buyerCompleted.length,
    buyer_refunded_or_cancelled_count: buyerBadEnd.length,
    buyer_in_progress_count: buyerOpen.length,
    buyer_litiges_count: litigeCount,
    buyer_litiges_opened_by_buyer_count: litigesOpenedByBuyer,
    buyer_litiges_opened_by_seller_count: litigesOpenedBySeller,
    buyer_litige_rate_vs_completed: litigeRateVsCompleted,
    buyer_ratings_count: stars.length,
    buyer_avg_rating: avgStars != null ? Math.round(avgStars * 10) / 10 : null,
    last_90d: {
      buyer_transaction_count: buyerTx.filter((t) => isWithinLast90Days(t.created_at, nowMs)).length,
      buyer_completed_count: buyerCompleted.filter((t) => isWithinLast90Days(t.created_at, nowMs)).length,
    },
    alerts,
  };
}
